import { CsAsset } from '../../../../common/models/content-studio.model';
import { ProductNodeProduct } from './ProductNode';
import { GraphicsParams } from '../components/ImageEditorModal';
import type { VideoComposition } from '../video-editor/composition';
import type { VideoStoryState } from '../video-editor/video-story';

/**
 * The canvas graph.
 *
 * These types are the persisted shape of a Content Studio project — they are
 * written to `CsProject.canvasGraph` verbatim and read back on load, so a
 * change here is a data-format change.
 *
 * They live outside the workspace page because more than one thing builds a
 * graph now: the page itself, and the planner's compiler, which turns an
 * agent's proposed plan into nodes.
 */

export interface CanvasProductNode {
    kind: 'product';
    id: string;
    x: number;
    y: number;
    products: ProductNodeProduct[];
}

export interface CanvasRecipeNode {
    /**
     * Folded to its name and a Generate button.
     *
     * Set on nodes a plan created: four recipes' worth of settings buries the
     * pictures the vendor actually asked for. A node added by hand is a node
     * being configured, so it opens.
     */
    collapsed?: boolean;
    kind: 'recipe';
    id: string;
    x: number;
    y: number;
    productNodeId: string;
    recipeSlug: string;
    inputs: Record<string, any>;
    generationNodeIds: string[];
    generating: boolean;
    /**
     * Extra generation node IDs when the recipe was spawned from a
     * multi-select action (Create video from these). Drives additional
     * input edges on the canvas so the lineage reads as "N images →
     * this recipe". Empty / undefined for single-parent recipes.
     */
    additionalSourceNodeIds?: string[];
}

export interface CanvasGenerationNode {
    kind: 'generation';
    id: string;
    x: number;
    y: number;
    asset: CsAsset;
    recipeNodeId: string;
    approved?: boolean;
}


/**
 * Edit-image node — sits between a source generation and its edited
 * result on the canvas. Hosts the per-tool configuration (Expand today,
 * more to follow) and a Generate button that fires the underlying
 * recipe (expand-image, inpaint, etc.) chained from the source asset.
 */
export type EditImageTool = 'expand' | 'retouch' | 'modify' | 'graphics' | 'upscale' | 'design';

export interface RetouchRegion {
    /** Unique id for React list rendering and selection tracking. */
    id: string;
    /** Lasso polygon in the source image's native pixel space. Legacy rect
     *  regions are normalized to a 4-corner polygon when the editor loads. */
    points: { x: number; y: number }[];
    /** Bounding box (source px) — placement + legacy rect regions. */
    x: number;
    y: number;
    width: number;
    height: number;
    prompt: string;
}

export interface CanvasEditImageNode {
    kind: 'edit-image';
    /**
     * Folded to its tool and a Generate button.
     *
     * Set on nodes a plan created, for the same reason recipes are: a chain of
     * edits buries the pictures the vendor asked for under settings panels.
     */
    collapsed?: boolean;
    id: string;
    x: number;
    y: number;
    sourceNodeId: string;   // id of the parent generation node
    sourceAssetUuid: string;
    /** Which image-editing tool is active on this node. */
    tool: EditImageTool;
    /** Expand-tool inputs (pixel deltas per side + optional prompt). */
    expandInputs?: {
        extendTop: number;
        extendRight: number;
        extendBottom: number;
        extendLeft: number;
        prompt: string;
    };
    /** Retouch-tool inputs — one or more labeled rectangular regions. */
    retouchInputs?: {
        regions: RetouchRegion[];
    };
    /** Modify-tool inputs — freeform edit prompt + optional reference URL. */
    modifyInputs?: {
        prompt: string;
        referenceUrl?: string;
    };
    /** Graphics-tool inputs — text boxes + arrows + optional style reference + prompt. */
    graphicsInputs?: GraphicsParams;
    /** Upscale-tool scale factor (2 / 4). */
    upscaleScale?: number;
    /** Design-tool state — fabric scene + the rasterised composite. */
    designInputs?: {
        fabricJson?: any;
        renderedUrl?: string;
        width?: number;
        height?: number;
        /**
         * The words and placement a plan asked for, kept as written.
         *
         * The Fabric scene is the drawing; this is the intent. Without it the
         * agent could not see what a design already said, so "move the text to
         * the top" stacked a second design onto the first one's output instead
         * of changing it.
         */
        spec?: {
            headline: string;
            subheadline?: string;
            placement: 'top' | 'bottom' | 'left' | 'right' | 'center';
            textColor?: string;
            logoPlacement?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
        };
    };
    generationNodeIds: string[];
    generating: boolean;
}

/**
 * Design node — a free-form Canva-style editor layered on top of a
 * source generation. Unlike edit-image nodes which dispatch AI jobs to
 * the worker, design rendering happens 100% client-side via Fabric.js.
 * On Save the canvas is rasterized to a PNG, uploaded to S3 (via the
 * style-ref endpoint), and the resulting URL is stored on the node so
 * the rendered output displays directly. The fabric scene graph is
 * persisted so re-opening the editor restores every layer exactly.
 */
export interface CanvasDesignNode {
    kind: 'design';
    id: string;
    x: number;
    y: number;
    sourceNodeId: string;           // id of the parent generation node
    sourceAssetUuid: string;        // uuid of the background image asset
    /**
     * Extra generation node IDs when the design was spawned from a
     * multi-select action. Drives additional input edges on the
     * canvas so the lineage reads as "N images → this design".
     * Empty / undefined for single-parent designs.
     */
    additionalSourceNodeIds?: string[];
    /** Serialized Fabric scene — everything needed to re-open the editor
     *  with the same layers / text / fonts / positions / z-order. */
    fabricJson?: any;
    /** CDN URL of the last-rendered PNG (after Save). Displayed inline
     *  on the node as the "current output" thumbnail. */
    renderedUrl?: string;
    /** Native dimensions of the rendered output, used for downstream
     *  aspect-ratio preservation. */
    renderedDimensions?: { width: number; height: number };
    /** True while the PNG is being rasterized + uploaded. */
    saving: boolean;
}

/**
 * WallPicker node — sits between a lifestyle-scene Recipe (surface=walls)
 * and any Generation nodes spawned from it. The upstream "setup" phase
 * generation produces an analyzed Space; this node holds that asset and
 * lets the vendor pick which walls to texture before each render. Each
 * Continue press spawns a fresh sibling Generation downstream so the
 * vendor can revisit the picker, change the selection, and compare
 * results without losing prior renders.
 */
export interface CanvasWallPickerNode {
    kind: 'wall-picker';
    id: string;
    x: number;
    y: number;
    /** Recipe that drives this picker (carries inputs forward to render). */
    recipeNodeId: string;
    /** Setup-phase asset whose `wallPicker` payload populates the markers. */
    asset: CsAsset;
    /** Wall ids the vendor currently has selected (defaults to all on load). */
    selectedWallIds: number[];
    /** Render-phase generations spawned from this picker. */
    generationNodeIds: string[];
    /** True while a Continue press is in flight (createGeneration roundtrip). */
    generating: boolean;
}

/**
 * Video edit node — a timeline (clips, captions, music) composed in the
 * browser editor and rendered client-side. Its composition is the editor's
 * whole state; each export uploads an MP4 and lands as a generation node
 * downstream (`generationNodeIds`), so earlier cuts stay on the canvas.
 */
export interface CanvasVideoEditNode {
    kind: 'video-edit';
    id: string;
    x: number;
    y: number;
    composition: VideoComposition;
    /** Generation nodes the timeline was started from — drawn as input edges. */
    sourceNodeIds?: string[];
    /** Exports, oldest first. */
    generationNodeIds: string[];
    renderedAssetUuid?: string;
    renderedUrl?: string;
    renderedThumbnailUrl?: string;
    /**
     * Set when this timeline is a Video Story: the storyboard, which canvas
     * nodes shoot and animate each scene, and the music. The timeline is built
     * from them once every scene has landed.
     */
    story?: VideoStoryState;
}

export type CanvasNode =
    | CanvasProductNode
    | CanvasRecipeNode
    | CanvasGenerationNode
    | CanvasEditImageNode
    | CanvasDesignNode
    | CanvasWallPickerNode
    | CanvasVideoEditNode;

/**
 * Every node that dies when `rootId` is removed, including `rootId` itself.
 *
 * Parent→child edges in this graph:
 *   product    → recipe      (recipe.productNodeId)
 *   recipe     → generation  (generation.recipeNodeId)
 *   generation → recipe      (productNodeId doubles as "parent node" when a
 *                             variation or image-to-video chains off an asset)
 *   generation → edit-image  (editImage.sourceNodeId)
 *   edit-image → generation  (generation.recipeNodeId points at the edit node)
 *   recipe     → wall-picker (wallPicker.recipeNodeId)
 *
 * Breadth-first so removing an upstream node takes its whole branch. Shared by
 * the canvas's own delete and by the planner's preview, which has to tell the
 * vendor how much a deletion actually costs before they accept it.
 */
export function collectNodeAndDescendants(
    nodes: CanvasNode[],
    rootId: string,
): Set<string> {
    const doomed = new Set<string>([rootId]);
    const queue: string[] = [rootId];

    while (queue.length > 0) {
        const currentId = queue.shift()!;
        for (const n of nodes) {
            if (doomed.has(n.id)) continue;
            const isChild =
                (n.kind === 'recipe' && (n as CanvasRecipeNode).productNodeId === currentId)
                || (n.kind === 'generation' && (n as CanvasGenerationNode).recipeNodeId === currentId)
                || (n.kind === 'edit-image' && (n as CanvasEditImageNode).sourceNodeId === currentId)
                || (n.kind === 'design' && (n as CanvasDesignNode).sourceNodeId === currentId)
                || (n.kind === 'wall-picker' && (n as CanvasWallPickerNode).recipeNodeId === currentId);
            if (isChild) {
                doomed.add(n.id);
                queue.push(n.id);
            }
        }
    }
    return doomed;
}
